import { useEffect } from 'react'
import { CommandBar } from './components/CommandBar'
import { TimeScrubber } from './components/TimeScrubber'
import { ToolRail } from './components/ToolRail'
import { MapCanvas } from './components/MapCanvas'
import { LayersPanel } from './components/LayersPanel'
import { DetailPanel } from './components/DetailPanel'
import { LogDock } from './components/LogDock'
import { StatusBar } from './components/StatusBar'
import { CommandPalette } from './components/CommandPalette'
import { useDeck } from './state/store'
import { resolveCourseTarget } from './lib/course'
import { connectStream, fetchScenario, fetchWhoAmI } from './lib/gateway'
import { loadBundleManifest } from './lib/bundle'

/** Below 1024px an operational-level battlespace cannot be usefully displayed.
 *  Saying so is more honest than shipping a layout that serves nobody
 *  (DESIGN-SYSTEM.md §4). Multi-monitor is the real answer — FR-UI-13. */
function ViewportNotice() {
  return (
    <div className="flex h-full w-full items-center justify-center p-8 lg:hidden">
      <div className="panel max-w-sm p-5 text-center">
        <p style={{ fontSize: 13, color: 'var(--color-text-1)' }}>
          ORBIS Deck requires a viewport of at least 1024px.
        </p>
        <p className="mt-2" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
          This is an operator console, not a mobile app. An operational-level battlespace
          cannot be usefully rendered below this width.
        </p>
      </div>
    </div>
  )
}

export default function App() {
  const running = useDeck((s) => s.running)
  const tick = useDeck((s) => s.tick)
  const toggleRunning = useDeck((s) => s.toggleRunning)
  const toggleLayersPanel = useDeck((s) => s.toggleLayersPanel)
  const toggleLog = useDeck((s) => s.toggleLog)
  const setPaletteOpen = useDeck((s) => s.setPaletteOpen)
  const setViewMode = useDeck((s) => s.setViewMode)
  const select = useDeck((s) => s.select)
  const setTool = useDeck((s) => s.setTool)
  const measureClear = useDeck((s) => s.measureClear)
  const setDemoStep = useDeck((s) => s.setDemoStep)
  const courseClear = useDeck((s) => s.courseClear)
  const toggleMissions = useDeck((s) => s.toggleMissions)
  const toggleOrbat = useDeck((s) => s.toggleOrbat)
  const toggleLosses = useDeck((s) => s.toggleLosses)
  const toggleInterop = useDeck((s) => s.toggleInterop)
  const toggleScenario = useDeck((s) => s.toggleScenario)
  const toggleAnalysis = useDeck((s) => s.toggleAnalysis)

  // ── The real simulation ────────────────────────────────────────────────────
  //
  // Snapshots from the kernel are the authoritative state. Mounted once, with a disposer,
  // because React Strict Mode mounts effects twice in development and two live sockets would
  // double-apply every snapshot.
  useEffect(() => {
    // The bundle manifest, before anything draws. In bundle mode the map's style is built FROM
    // it, so a map created before it arrives would render the fallback and only correct itself on
    // the next basemap change — which, for a deployment whose whole point is that it has no
    // upstream, looks exactly like the bundle not working.
    void loadBundleManifest().then((manifest) => {
      if (manifest) useDeck.getState().setBundle(manifest)
    })

    return connectStream({
      onSnapshot: (snap) => useDeck.getState().applySnapshot(snap),
      onEvents: (events) => useDeck.getState().appendEvents(events),
      onState: (state) => {
        useDeck.getState().setConnection(state)
        // Fetched on every (re)connect rather than once at mount: a Gateway restart can bring a
        // different dataset, and stale class names would be worse than none.
        if (state === 'live') {
          void fetchScenario().then((info) => {
            if (info) useDeck.getState().applyScenario(info)
          })
          void fetchWhoAmI().then((me) => useDeck.getState().setMe(me))
        } else {
          useDeck.getState().setMe(null)
        }
      },
    })
  }, [])

  // Fallback clock, used ONLY when the Gateway is unreachable, so the display does not look
  // frozen while offline. `tick` is a no-op while a snapshot stream is live — the client must
  // never become a second source of truth for simulated time (SPEC-01 §2.2 D5).
  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => tick(1), 1000)
    return () => window.clearInterval(id)
  }, [running, tick])

  // Keyboard-first for experts; nothing REQUIRES a shortcut (Principle 7)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return

      switch (e.key) {
        case ' ':
          e.preventDefault()
          toggleRunning()
          break
        case 'l':
        case 'L':
          toggleLayersPanel()
          break
        case '3':
          setViewMode('3d')
          break
        case '2':
          setViewMode('2d')
          break
        case 'o':
        case 'O':
          toggleMissions()
          break
        case 'b':
        case 'B':
          toggleOrbat()
          break
        case 'x':
        case 'X':
          toggleLosses()
          break
        // I for Interop. The DIS panel is diagnostics, so it is a shortcut rather than a
        // permanent fixture — an operator opens it when a federate says it cannot see us.
        case 'i':
        case 'I':
          toggleInterop()
          break
        // N for sceNario. S, C and L were all taken by tools already.
        case 'n':
        case 'N':
          toggleScenario()
          break
        // Y for analYsis. A is the annotate tool.
        case 'y':
        case 'Y':
          toggleAnalysis()
          break
        case '`':
          toggleLog()
          break
        case 'Escape':
          // Escape unwinds one layer at a time rather than clearing everything —
          // an operator pressing Esc to drop a measurement should not also lose
          // their selection.
          if (useDeck.getState().demoStep !== null) setDemoStep(null)
          else if (useDeck.getState().measure.from) measureClear()
          else {
            // Clears only the course the operator is actually working on. Escape used to
            // clear "the" course when there was only one; now that every unit keeps its own
            // plan, clearing all of them on one keystroke would destroy work that is not
            // even in the foreground.
            const { selection: sel, courses, courseFocus } = useDeck.getState()
            const target = resolveCourseTarget(sel, courses, courseFocus)
            if (target.entityId && target.waypoints.length) courseClear(target.entityId)
            else select(null)
          }
          break
        case 'v':
        case 'V':
          setTool('select')
          break
        case 'c':
        case 'C':
          setTool('course')
          break
        case 'm':
        case 'M':
          setTool('measure')
          break
        case 'r':
        case 'R':
          setTool('refpoint')
          break
        case 'a':
        case 'A':
          setTool('annotate')
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleRunning, toggleLayersPanel, toggleLog, setPaletteOpen, select, setTool, setViewMode, measureClear, setDemoStep, courseClear, toggleMissions, toggleOrbat, toggleLosses, toggleInterop, toggleScenario, toggleAnalysis])

  return (
    <>
      <ViewportNotice />

      <div className="hidden h-full flex-col lg:flex">
        <CommandBar />
        <TimeScrubber />

        <div className="flex min-h-0 flex-1">
          <ToolRail />

          <main className="relative flex min-w-0 flex-1 flex-col">
            <div className="relative min-h-0 flex-1">
              <MapCanvas />
              <LayersPanel />
            </div>
            <LogDock />
          </main>

          <DetailPanel />
        </div>

        <StatusBar />
        <CommandPalette />
      </div>
    </>
  )
}
